import { AfterViewInit, Component, computed, effect, ElementRef, inject, input, output, signal, viewChild } from '@angular/core';
import { ThemeColor } from '../helpers';
import { ModalService } from './modal.service';

type DismissableFunction = () => boolean | Promise<boolean>;
type ModalSize = 'sm' | 'md' | 'fs' | 'lg' | 'xl';

@Component({
  selector: 'app-pan-modal',
  templateUrl: './pan-modal.component.html',
  styleUrl: './pan-modal.component.scss',
  standalone: false,
})
export class PanModalComponent implements AfterViewInit {
  private modalService = inject(ModalService);
  private modal = viewChild<ElementRef<HTMLDialogElement>>('modal');
  private lastFocusedElement: HTMLElement | null = null;
  public headerId = `pan-modal-header-${Math.random().toString(36).slice(2)}`;
  public bodyId = `pan-modal-body-${Math.random().toString(36).slice(2)}`;

  public isModalCurrentlyOpen = signal<boolean>(false);

  public color = input<ThemeColor>('default');
  public isOpen = input<boolean | undefined>();
  public canDismiss = input<DismissableFunction>(() => true);
  public canDismissOnBackdropClick = input<boolean>(true);
  public topLevel = input<boolean>(true);
  public size = input<ModalSize>('md');
  public shouldBodyScroll = input<boolean>(false);
  public maxBodyHeight = input<string>('');

  public modalWillOpen = output<void>();
  public modalOpen = output<void>();
  public modalWillClose = output<void>();
  public modalClose = output<void>();

  public classes = computed(() => {
    const classes = ['modal', `modal-color-${this.color()}`, `modal-size-${this.size()}`];
    if (!this.topLevel()) {
      classes.push('modal-non-top-level');
    }
    return classes;
  });

  constructor() {
    effect(() => {
      const isOpen = this.isOpen();
      if (isOpen === true) {
        this.show();
      }

      if (isOpen === false) {
        this.hide();
      }
    });
  }

  ngAfterViewInit() {
    this.watchEscape();
    this.watchOutsideClicks();
  }

  private watchOutsideClicks() {
    this.modal()?.nativeElement.addEventListener('click', (event) => {
      if (event.target !== this.modal()?.nativeElement) return;

      this.attemptToCloseByClickingOutside();
    });
  }

  private watchEscape() {
    this.modal()?.nativeElement.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      void this.modalService.removeModal();
    });
  }

  private focusInitialElement() {
    const modal = this.modal()?.nativeElement;
    if (!modal) return;

    const focusTarget = modal.querySelector(
      [
        'button:not([disabled])',
        '[href]',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(', '),
    ) as HTMLElement | null;

    if (focusTarget) {
      focusTarget.focus();
      return;
    }

    modal.focus();
  }

  public show() {
    this.modalWillOpen.emit();
    this.lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // this won't work well with other pop-up like components (e.g. datepickers)
    if (this.topLevel()) {
      this.modal()?.nativeElement.showModal();
    }
    // this one does, but we'll have to fix position ourselves & add backdrop ourselves
    else {
      this.modal()?.nativeElement.show();
    }
    this.modalOpen.emit();
    this.modalService.addModal(this);

    this.isModalCurrentlyOpen.set(true);
    queueMicrotask(() => this.focusInitialElement());
  }

  public hide() {
    this.modalWillClose.emit();
    this.modal()?.nativeElement.close();
    this.modalClose.emit();
    this.isModalCurrentlyOpen.set(false);
    this.lastFocusedElement?.focus();
    this.lastFocusedElement = null;
  }

  public attemptToCloseByClickingOutside() {
    if (!this.canDismissOnBackdropClick()) return;

    this.modalService.removeModal();
  }
}
